import { supabase } from '../../../lib/supabase';

/**
 * Zona horaria de México (CDMX) para el ciclo semanal.
 */
const MEXICO_TZ = 'America/Mexico_City';
const MAX_SCORE = 2000;
const SUBMIT_URL = '/api/submit-score';
const LOCAL_SUBMIT_COOLDOWN_MS = 15000;

/**
 * Get the start of the current leaderboard cycle as an ISO timestamp (UTC).
 * Weekly cycle: Wednesday 00:00 → Tuesday 23:59 (Mexico City time).
 * This aligns with the countdown timer reset on Tuesday at 23:59.
 *
 * Returns an ISO-8601 string in UTC (e.g. "2026-02-18T06:00:00.000Z")
 * that represents Wednesday 00:00 CDMX of the current cycle.
 */
function getCycleStartUTC() {
  // Obtener la fecha/hora actual en CDMX
  const mxNow = new Date(
    new Date().toLocaleString('en-US', { timeZone: MEXICO_TZ })
  );
  const day = mxNow.getDay(); // 0=Dom, 1=Lun, 2=Mar, 3=Mié...

  // Cuántos días restar para llegar al miércoles anterior (o actual)
  let diff;
  if (day >= 3) {
    diff = day - 3;
  } else {
    diff = day + 4; // 0→4, 1→5, 2→6
  }

  const wednesday = new Date(mxNow);
  wednesday.setDate(wednesday.getDate() - diff);
  wednesday.setHours(0, 0, 0, 0);

  // Convertir miércoles 00:00 CDMX → UTC
  // Obtenemos el offset actual de CDMX
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: MEXICO_TZ,
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(new Date());
  const offsetPart = parts.find((p) => p.type === 'timeZoneName');
  const offsetMatch = offsetPart?.value.match(/GMT([+-]\d+)/);
  const offsetHours = offsetMatch ? parseInt(offsetMatch[1], 10) : -6;

  // Construir UTC Date
  const ymd = `${wednesday.getFullYear()}-${String(wednesday.getMonth() + 1).padStart(2, '0')}-${String(wednesday.getDate()).padStart(2, '0')}`;
  const utc = new Date(`${ymd}T00:00:00Z`);
  utc.setHours(utc.getHours() - offsetHours); // e.g. -(-6) = +6 → 06:00 UTC

  return utc.toISOString();
}

/**
/**
 * Obtiene los componentes de fecha (year, month, day, dow) en la zona
 * horaria de México, sin el bug de UTC que desfasa un día después de
 * medianoche UTC (= 6pm CST). Usa Intl.DateTimeFormat para leer los
 * componentes directamente en la timezone correcta.
 */
function getMexicoDateParts() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MEXICO_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  }).formatToParts(now);
  const year  = Number(parts.find(p => p.type === 'year').value);
  const month = Number(parts.find(p => p.type === 'month').value);
  const day   = Number(parts.find(p => p.type === 'day').value);
  const WD_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = WD_MAP[parts.find(p => p.type === 'weekday').value];
  return { year, month, day, dow };
}

/**
 * Wednesday-based week_start for new inserts.
 * Usa Intl para leer la fecha en México y Date.UTC para la aritmética,
 * evitando el desfase de +1 día que ocurre después de las 6pm CST.
 */
function getWeekStart() {
  const { year, month, day, dow } = getMexicoDateParts();
  const diff = dow >= 3 ? dow - 3 : dow + 4;
  // Aritmética pura en UTC para evitar offsets de timezone
  const wedTs = Date.UTC(year, month - 1, day - diff);
  const wed = new Date(wedTs);
  const yyyy = wed.getUTCFullYear();
  const mm = String(wed.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(wed.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Monday-based week_start (sistema anterior). Necesario para mostrar
 * entradas guardadas antes de la migración al ciclo miércoles-martes.
 */
function getISOMonday() {
  const { year, month, day, dow } = getMexicoDateParts();
  const diff = dow === 0 ? 6 : dow - 1;
  const monTs = Date.UTC(year, month - 1, day - diff);
  const mon = new Date(monTs);
  const yyyy = mon.getUTCFullYear();
  const mm = String(mon.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(mon.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Fetch top 3 scores for the current weekly cycle.
 * Matches entries by week_start using BOTH the old Monday-based key
 * and the new Wednesday-based key, so no scores are lost during
 * the transition to the new cycle system.
 * @returns {Promise<Array<{alias: string, score: number, created_at: string}>>}
 */
export async function getWeeklyTop10() {
  const wednesday = getWeekStart();
  const monday = getISOMonday();

  const { data, error } = await supabase
    .from('leaderboard')
    .select('alias, score, created_at')
    .or(`week_start.eq.${wednesday},week_start.eq.${monday}`)
    .order('score', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(200);

  if (error) {
    console.error('[Leaderboard] Error fetching top 10:', error.message);
    return [];
  }

  const rows = data || [];
  const uniqueByAlias = new Map();

  for (const row of rows) {
    const key = String(row.alias || '').trim().toLowerCase();
    if (!key) continue;
    if (!uniqueByAlias.has(key)) {
      uniqueByAlias.set(key, row);
    }
  }

  return Array.from(uniqueByAlias.values()).slice(0, 3);
}

function sanitizeAlias(rawAlias) {
  const trimmed = String(rawAlias || '').trim();
  if (!trimmed) return '';
  const withPrefix = trimmed.startsWith('@') ? trimmed : `@${trimmed.replace(/@/g, '')}`;
  const normalized = `@${withPrefix.slice(1).replace(/[^a-zA-Z0-9_]/g, '')}`;
  return normalized.slice(0, 16);
}

function isValidAlias(alias) {
  return /^@[a-zA-Z0-9_]{1,15}$/.test(alias);
}

function isValidRunMeta(runMeta) {
  if (!runMeta || typeof runMeta !== 'object') return false;

  const durationMs = Number(runMeta.durationMs);
  const jumps = Number(runMeta.jumps);
  const coins = Number(runMeta.coins);
  const platformBonuses = Number(runMeta.platformBonuses);
  const scoreFromMeta = Number(runMeta.scoreFromMeta);
  const endedBy = String(runMeta.endedBy || '');

  if (!Number.isFinite(durationMs) || durationMs < 0 || durationMs > 31000) return false;
  if (!Number.isInteger(jumps) || jumps < 0 || jumps > 120) return false;
  if (!Number.isInteger(coins) || coins < 0 || coins > 80) return false;
  if (!Number.isInteger(platformBonuses) || platformBonuses < 0 || platformBonuses > 80) return false;
  if (!Number.isInteger(scoreFromMeta) || scoreFromMeta < 0 || scoreFromMeta > MAX_SCORE) return false;
  if (endedBy !== 'time' && endedBy !== 'fall') return false;

  return true;
}

function underLocalCooldown(alias) {
  try {
    const key = `doggy_jump_submit_${alias.toLowerCase()}`;
    const raw = localStorage.getItem(key);
    const last = raw ? Number(raw) : 0;
    if (Number.isFinite(last) && Date.now() - last < LOCAL_SUBMIT_COOLDOWN_MS) {
      return true;
    }
    localStorage.setItem(key, String(Date.now()));
  } catch (_) {
    return false;
  }
  return false;
}

/**
 * Submit a score to the leaderboard via secure backend API.
 * @param {string} alias - Player alias (@handle, 2-16 chars)
 * @param {number} score - Score (0-2000)
 * @param {object|null} runMeta - Game run metadata for validation
 * @param {string} turnstileToken - Cloudflare Turnstile CAPTCHA token
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function submitScore(alias, score, runMeta = null, turnstileToken = '') {
  // Client-side pre-validation (real validation happens server-side)
  const cleanAlias = sanitizeAlias(alias);
  if (!isValidAlias(cleanAlias)) {
    return { success: false, error: 'Alias inválido. Usa formato @usuario (1-15 caracteres).' };
  }
  if (!Number.isFinite(score) || score < 0 || score > MAX_SCORE) {
    return { success: false, error: 'Score inválido' };
  }
  if (!isValidRunMeta(runMeta)) {
    return { success: false, error: 'Partida inválida. Juega una nueva ronda.' };
  }
  if (Math.round(score) !== runMeta.scoreFromMeta) {
    return { success: false, error: 'Score inconsistente. Intenta de nuevo.' };
  }
  if (underLocalCooldown(cleanAlias)) {
    return { success: false, error: 'Espera unos segundos antes de volver a enviar.' };
  }

  const weekStart = getWeekStart();
  const roundedScore = Math.round(score);

  try {
    const res = await fetch(SUBMIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        alias: cleanAlias,
        score: roundedScore,
        weekStart,
        runMeta,
        turnstileToken,
      }),
    });

    const payload = await res.json().catch(() => null);
    if (!res.ok || !payload?.success) {
      return { success: false, error: payload?.error || 'No se pudo validar el puntaje.' };
    }
    // `saved` indica si el score fue efectivamente guardado (true) o si ya
    // existía un puntaje mayor para ese alias esta semana (false).
    // `bestScore` es el mejor puntaje real que tiene el alias en la BD.
    return {
      success: true,
      saved: payload.saved !== false,          // true por defecto si API antigua
      bestScore: payload.bestScore ?? roundedScore,
    };
  } catch (error) {
    console.error('[Leaderboard] Submit failed:', error?.message || error);
    return { success: false, error: 'No se pudo conectar con el servidor. Intenta de nuevo.' };
  }
}

/**
 * Check if a score would qualify for the current weekly top 3.
 * @param {number} score
 * @returns {Promise<boolean>}
 */
export async function qualifiesForTop10(score) {
  const top = await getWeeklyTop10();
  if (top.length < 3) return true;
  const lowestTop10 = top[top.length - 1].score;
  return score >= lowestTop10;
}
